import type { WorklistFilterParams } from '@/Models/GenericWorklist/WorklistParams';
import type { WorklistFilterProperty } from './WorklistProperties';

/**
 * Maps each WorklistFilterProperty to the WorklistFilterParams keys it controls.
 */
export const FILTER_PROPERTY_PARAM_KEYS: Record<WorklistFilterProperty, readonly (keyof WorklistFilterParams)[]> = {
    AccessionNumber: ['AccessionNumber'],
    DateRange: ['DateRangePreset', 'From', 'Until'],
    DoB: ['DoB'],
    ExternalID: ['ExternalID'],
    InvoiceStatuses: ['InvoiceStatuses'],
    Justification: ['Justification'],
    LabelsToExcludeIDs: ['LabelsToExcludeIDs'],
    LabelsToIncludeIDs: ['LabelsToIncludeIDs'],
    Language: ['Language'],
    ModalityIDs: ['ModalityIDs'],
    NationalNumber: ['PatientNationalNumber'],
    OrderNumber: ['OrderNumber'],
    OrderStatus: ['OrderStatuses'],
    PatientFirstName: ['PatientFirstName'],
    PatientLastName: ['PatientLastName'],
    PatientOrigin: ['PatientOrigins'],
    PatientStatus: ['PatientStatuses'],
    ProcedureCodes: ['ProcedureCodes'],
    ProcedureDescription: ['ProcedureDescription'],
    ProcedureStatus: ['ProcedureStatuses'],
    Radiologists: ['Radiologists'],
    ReadyForInvoicing: ['ReadyForInvoicing'],
    ReportAvailable: ['ReportAvailable'],
    ReportCreatedDateRange: ['ReportCreatedDateRangePreset', 'ReportCreatedFrom', 'ReportCreatedUntil'],
    ReportLocked: ['ReportLocked'],
    ReportModifiedBy: ['ReportModifiedBy'],
    ReportModifiedDateRange: ['ReportModifiedDateRangePreset', 'ReportModifiedFrom', 'ReportModifiedUntil'],
    ReportParticipantType: ['ReportParticipantTypes'],
    ReportParticipants: ['ReportParticipants'],
    ReportStatuses: ['ReportStatuses'],
    RequestingPhysicianFirstName: ['RequestingPhysicianFirstName'],
    RequestingPhysicianLastName: ['RequestingPhysicianLastName'],
    RoomsIDs: ['RoomIDs'],
    SiteIDs: ['SiteIDs'],
    Technicians: ['Technicians'],
    Transcriptionists: ['Transcriptionists'],
    UnitOfCare: ['UnitOfCare'],
    UrgencyIds: ['UrgencyIDs'],
    VisitNumber: ['VisitNumber'],
};

/**
 * Returns a shallow copy of `filter` with all fields belonging to the given hidden
 * properties removed. Returns the original reference unchanged when there is nothing
 * to clear, so callers can use reference equality to detect no-ops.
 */
export const clearHiddenFilterFields = (
    filter: WorklistFilterParams | undefined,
    hiddenProperties: readonly WorklistFilterProperty[],
): WorklistFilterParams | undefined => {
    if (filter === undefined || hiddenProperties.length === 0) {
        return filter;
    }

    const result = { ...filter };
    for (const property of hiddenProperties) {
        for (const key of FILTER_PROPERTY_PARAM_KEYS[property]) {
            delete result[key];
        }
    }
    return result;
};
